// admin-spend.tsx — "Cloud & AI Spend Observability — The Meter" admin microtab
// (docs/cloud-cost-observability-prd.md). Convention: this is an admin-console
// page, so it follows the established admin dark palette (hardcoded #0A0A0A /
// white-alpha utilities) used by admin-prds / admin-investors, and is therefore
// intentionally NOT registered in check-contrast.mjs RULE3_FILES — that rule is
// the ramp for the day/night portal surfaces, not the admin console. Brand
// orange (#FF5C00 / #E55200) carries identity in both modes.
//
// Every USD figure shown here is an ESTIMATE derived from token counts + a code
// price table, NOT a provider invoice (SHIPPING_CHECKLIST I8). The UI says so.

import { useEffect, useState, useCallback } from "react";
import {
  Gauge, Loader2, RefreshCw, AlertTriangle, ShieldCheck, ShieldAlert, Zap,
  Bot, Cpu, Users, Mail, HardDrive, Cloud, Send, Settings2, Power, History,
} from "lucide-react";
import ProgressLoader from "@/components/progress-loader";

const BASE = import.meta.env.BASE_URL;

function authHeaders() {
  const token = localStorage.getItem("admin_token") || "";
  return { "Content-Type": "application/json", "x-admin-token": token };
}

const usd = (n: number) =>
  `$${(Number.isFinite(n) ? n : 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usd4 = (n: number) =>
  `$${(Number.isFinite(n) ? n : 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const num = (n: number) => (Number.isFinite(n) ? n : 0).toLocaleString("en-US");
const fmtBytes = (b: number) => {
  if (!b) return "0 B";
  const k = 1024, units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(b) / Math.log(k)));
  return `${(b / k ** i).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
};

// ---- contract types (mirror routes/admin-spend.ts) -------------------------
interface SpendBucket { key: string; estUsd: number; inputTokens: number; outputTokens: number; calls: number }
interface BreakerState {
  tripped: boolean; reason: "manual" | "auto" | null; warned: boolean;
  spentToday: number; ceilingUsd: number; warnUsd: number; day: string;
  manualOverride: boolean; manualTripped: boolean; mode: string;
}
interface Summary {
  estimated: true; note: string;
  ai: { today: number; last7d: number; last30d: number; byAgent: SpendBucket[]; byModel: SpendBucket[]; byTrigger: SpendBucket[] };
  breaker: BreakerState;
  estimates: {
    email: { today: { count: number; estUsd: number }; last7d: { count: number; estUsd: number }; last30d: { count: number; estUsd: number }; estimated: true; note: string };
    storage: { pricePerGbMonthUsd: number; totalBytes: number; totalGb: number; estUsdPerMonth: number; sources: { intakeDocuments: number; bankerArtifacts: number }; estimated: true; partial: true; note: string };
  };
  generatedAt: string;
}
type GcpState =
  | { connected: false; reason: string; setupSteps: string[]; requiredEnv: string[] }
  | { connected: true; currency: string; last30dUsd: number; byService: { service: string; costUsd: number }[]; windowStart: string; windowEnd: string; generatedAt: string; note: string };
interface TeamMember { id: number; name: string; email: string; role: string }
interface RiskEmailRow { id: number; toEmail: string; subject: string; sentAt: string; metadata: any }

export default function AdminSpend() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [gcp, setGcp] = useState<GcpState | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [sRes, gRes] = await Promise.all([
        fetch(`${BASE}api/admin/spend/summary`, { headers: authHeaders() }),
        fetch(`${BASE}api/admin/spend/gcp`, { headers: authHeaders() }),
      ]);
      if (sRes.status === 401) { setLoadError("Your admin session expired. Please log in again."); setLoading(false); return; }
      if (sRes.ok) setSummary(await sRes.json());
      else setLoadError(`Failed to load spend summary (${sRes.status}).`);
      if (gRes.ok) setGcp(await gRes.json());
    } catch { setLoadError("Network error loading spend data."); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 md:p-8 space-y-6 -mx-2 md:mx-0">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF5C00]/30 to-amber-500/20 border border-[#FF5C00]/30 flex items-center justify-center flex-shrink-0">
          <Gauge className="w-6 h-6 text-[#FF5C00]" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold text-white">The Meter · Cloud &amp; AI Spend</h1>
          <p className="text-white/55 text-sm mt-1">
            Token-derived AI spend, email &amp; storage estimates, and a daily budget breaker. Every figure here is an <span className="text-amber-300 font-medium">estimate</span> from code price tables — not a provider invoice.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="bg-white/[0.06] hover:bg-white/[0.1] text-white/80 border border-white/15 text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5 disabled:opacity-50 flex-shrink-0">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
        </button>
      </div>

      {loadError && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300">{loadError}</div>}

      {loading && !summary ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-white/[0.04] rounded-xl animate-pulse" />)}
        </div>
      ) : summary ? (
        <>
          <BreakerPanel breaker={summary.breaker} onChanged={load} />
          <AiSpendSection ai={summary.ai} />
          <EstimatesSection estimates={summary.estimates} />
          <GcpCard gcp={gcp} />
          <RiskEmailSection breaker={summary.breaker} />
          <p className="text-white/30 text-[11px] font-mono text-right">Generated {new Date(summary.generatedAt).toLocaleString()} · IST day {summary.breaker.day}</p>
        </>
      ) : null}
    </div>
  );
}

// =============================================================================
// Breaker panel — state, ceiling/warn inline edit, manual trip + override
// =============================================================================
function BreakerPanel({ breaker, onChanged }: { breaker: BreakerState; onChanged: () => void }) {
  const [ceiling, setCeiling] = useState(String(breaker.ceilingUsd));
  const [warn, setWarn] = useState(String(breaker.warnUsd));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { setCeiling(String(breaker.ceilingUsd)); setWarn(String(breaker.warnUsd)); }, [breaker.ceilingUsd, breaker.warnUsd]);

  const pct = breaker.ceilingUsd > 0 ? Math.min(100, (breaker.spentToday / breaker.ceilingUsd) * 100) : 0;
  const barColor = breaker.tripped ? "bg-red-500" : breaker.warned ? "bg-amber-400" : "bg-[#FF5C00]";

  const patch = async (body: Record<string, unknown>, label: string) => {
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`${BASE}api/admin/spend/breaker`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); setErr(e.error || `${label} failed (${r.status}).`); }
      else onChanged();
    } catch { setErr("Network error."); }
    setSaving(false);
  };

  const saveLimits = () => {
    const c = Number(ceiling), w = Number(warn);
    if (!Number.isFinite(c) || c <= 0) { setErr("Ceiling must be a positive number."); return; }
    if (!Number.isFinite(w) || w <= 0) { setErr("Warn must be a positive number."); return; }
    if (w > c) { setErr("Warn must be ≤ ceiling."); return; }
    patch({ ceilingUsd: c, warnUsd: w }, "Save limits");
  };

  const status = breaker.tripped
    ? { icon: <ShieldAlert className="w-5 h-5" />, label: breaker.reason === "manual" ? "HALTED — manual trip" : "HALTED — auto (ceiling hit)", cls: "text-red-300 border-red-500/40 bg-red-500/10" }
    : breaker.manualOverride
      ? { icon: <ShieldAlert className="w-5 h-5" />, label: "OVERRIDE ON — breaker bypassed", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" }
      : breaker.warned
        ? { icon: <AlertTriangle className="w-5 h-5" />, label: "WARNING — past warn threshold", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" }
        : { icon: <ShieldCheck className="w-5 h-5" />, label: "Healthy — under budget", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" };

  return (
    <div className="bg-white/[0.03] border border-white/[0.1] rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Power className="w-4 h-4 text-[#FF5C00]" />
        <h2 className="text-lg font-semibold text-white">Daily budget breaker</h2>
        <span className={`text-[11px] uppercase tracking-wider font-mono px-2 py-1 rounded border flex items-center gap-1.5 ${status.cls}`}>{status.icon}{status.label}</span>
        <span className="ml-auto text-[10px] uppercase tracking-wider font-mono text-white/40">mode: {breaker.mode}</span>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-2xl font-bold text-white tabular-nums">{usd(breaker.spentToday)}</span>
          <span className="text-xs text-white/50 font-mono">of {usd(breaker.ceilingUsd)} ceiling · warn at {usd(breaker.warnUsd)}</span>
        </div>
        <div className="h-2.5 rounded-full bg-white/[0.08] overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[11px] text-white/40 mt-1.5 font-mono">{pct.toFixed(1)}% of today&apos;s estimated AI budget (IST day)</p>
      </div>

      {err && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 text-xs text-red-300">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-white/45 font-mono flex items-center gap-1.5"><Settings2 className="w-3 h-3" /> Limits (USD/day)</p>
          <div className="flex items-end gap-2">
            <label className="flex-1">
              <span className="text-[10px] text-white/45">Ceiling</span>
              <input type="number" min="1" value={ceiling} onChange={e => setCeiling(e.target.value)} className="w-full mt-0.5 bg-white/[0.05] border border-white/15 rounded-lg px-2.5 py-1.5 text-sm text-white focus:border-[#FF5C00]/50 focus:outline-none tabular-nums" />
            </label>
            <label className="flex-1">
              <span className="text-[10px] text-white/45">Warn</span>
              <input type="number" min="1" value={warn} onChange={e => setWarn(e.target.value)} className="w-full mt-0.5 bg-white/[0.05] border border-white/15 rounded-lg px-2.5 py-1.5 text-sm text-white focus:border-[#FF5C00]/50 focus:outline-none tabular-nums" />
            </label>
            <button onClick={saveLimits} disabled={saving} className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-white/45 font-mono flex items-center gap-1.5"><Power className="w-3 h-3" /> Manual controls</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => patch({ manualTrip: !breaker.manualTripped }, "Manual trip")}
              disabled={saving}
              className={`text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5 border disabled:opacity-50 ${breaker.manualTripped ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25" : "bg-red-500/15 text-red-300 border-red-500/30 hover:bg-red-500/25"}`}>
              <ShieldAlert className="w-3.5 h-3.5" /> {breaker.manualTripped ? "Clear manual halt" : "Halt all AI now"}
            </button>
            <button
              onClick={() => patch({ manualOverride: !breaker.manualOverride }, "Override")}
              disabled={saving}
              className={`text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5 border disabled:opacity-50 ${breaker.manualOverride ? "bg-amber-500/20 text-amber-200 border-amber-500/40 hover:bg-amber-500/30" : "bg-white/[0.06] text-white/75 border-white/15 hover:bg-white/[0.1]"}`}>
              <Zap className="w-3.5 h-3.5" /> {breaker.manualOverride ? "Override ON (click to disable)" : "Override breaker"}
            </button>
          </div>
          <p className="text-[11px] text-white/40 leading-relaxed">Override forces AI to keep running even past the ceiling — use only when you accept the spend. It supersedes both auto and manual halts.</p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// AI spend — totals + breakdown by agent / model / human-vs-cron
// =============================================================================
function AiSpendSection({ ai }: { ai: Summary["ai"] }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Today (est.)" value={usd(ai.today)} icon={<Gauge className="w-4 h-4" />} />
        <StatCard label="Last 7 days (est.)" value={usd(ai.last7d)} icon={<Gauge className="w-4 h-4" />} />
        <StatCard label="Last 30 days (est.)" value={usd(ai.last30d)} icon={<Gauge className="w-4 h-4" />} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <BreakdownCard title="By agent" icon={<Bot className="w-4 h-4" />} buckets={ai.byAgent} total={ai.last30d} />
        <BreakdownCard title="By model" icon={<Cpu className="w-4 h-4" />} buckets={ai.byModel} total={ai.last30d} />
        <BreakdownCard title="Human vs cron" icon={<Users className="w-4 h-4" />} buckets={ai.byTrigger} total={ai.last30d} />
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
      <p className="text-[10px] uppercase tracking-wider text-white/50 font-mono flex items-center gap-1.5">{icon}{label}</p>
      <p className="text-2xl font-bold text-white mt-1.5 tabular-nums">{value}</p>
    </div>
  );
}

function BreakdownCard({ title, icon, buckets, total }: { title: string; icon: React.ReactNode; buckets: SpendBucket[]; total: number }) {
  const sorted = [...buckets].sort((a, b) => b.estUsd - a.estUsd).slice(0, 5);
  const max = sorted.reduce((m, b) => Math.max(m, b.estUsd), 0) || 1;
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
      <p className="text-[10px] uppercase tracking-wider text-white/50 font-mono flex items-center gap-1.5 mb-3">{icon}{title} <span className="text-white/30">· 30d, top 5</span></p>
      {sorted.length === 0 ? (
        <p className="text-white/40 text-xs py-4 text-center">No spend recorded yet.</p>
      ) : (
        <ul className="space-y-2.5">
          {sorted.map(b => (
            <li key={b.key}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-white/80 font-medium truncate pr-2">{b.key || "(unknown)"}</span>
                <span className="text-white/60 font-mono tabular-nums flex-shrink-0">{usd(b.estUsd)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                <div className="h-full bg-[#FF5C00]/70" style={{ width: `${(b.estUsd / max) * 100}%` }} />
              </div>
              <p className="text-[10px] text-white/35 font-mono mt-0.5">{num(b.calls)} calls · {num(b.inputTokens + b.outputTokens)} tok</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// =============================================================================
// Phase 2 — email + storage estimates
// =============================================================================
function EstimatesSection({ estimates }: { estimates: Summary["estimates"] }) {
  const { email, storage } = estimates;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
        <p className="text-[10px] uppercase tracking-wider text-white/50 font-mono flex items-center gap-1.5 mb-3"><Mail className="w-4 h-4" /> Email (est.)</p>
        <div className="grid grid-cols-3 gap-2 mb-2">
          {([["Today", email.today], ["7d", email.last7d], ["30d", email.last30d]] as const).map(([l, v]) => (
            <div key={l} className="bg-white/[0.03] rounded-lg p-2.5">
              <p className="text-[10px] text-white/45 font-mono">{l}</p>
              <p className="text-base font-bold text-white tabular-nums">{usd4(v.estUsd)}</p>
              <p className="text-[10px] text-white/40 font-mono">{num(v.count)} sent</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-white/40 leading-relaxed">{email.note}</p>
      </div>

      <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
        <p className="text-[10px] uppercase tracking-wider text-white/50 font-mono flex items-center gap-1.5 mb-3"><HardDrive className="w-4 h-4" /> Storage (est. <span className="text-amber-300">· partial</span>)</p>
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-2xl font-bold text-white tabular-nums">{usd4(storage.estUsdPerMonth)}</span>
          <span className="text-xs text-white/50 font-mono">/ month · {storage.totalGb} GB @ {usd4(storage.pricePerGbMonthUsd)}/GB-mo</span>
        </div>
        <p className="text-[11px] text-white/45 font-mono mb-2">Intake docs {fmtBytes(storage.sources.intakeDocuments)} · Banker artifacts {fmtBytes(storage.sources.bankerArtifacts)}</p>
        <p className="text-[11px] text-white/40 leading-relaxed">{storage.note}</p>
      </div>
    </div>
  );
}

// =============================================================================
// Phase 3 — GCP Cloud Billing (connect-state until creds exist)
// =============================================================================
function GcpCard({ gcp }: { gcp: GcpState | null }) {
  if (!gcp) return null;
  if (gcp.connected) {
    const max = gcp.byService.reduce((m, s) => Math.max(m, s.costUsd), 0) || 1;
    return (
      <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
        <p className="text-[10px] uppercase tracking-wider text-white/50 font-mono flex items-center gap-1.5 mb-3"><Cloud className="w-4 h-4 text-emerald-400" /> GCP Cloud spend · last 30d</p>
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-2xl font-bold text-white tabular-nums">{usd(gcp.last30dUsd)}</span>
          <span className="text-xs text-white/50 font-mono">{gcp.currency}</span>
        </div>
        <ul className="space-y-2">
          {[...gcp.byService].sort((a, b) => b.costUsd - a.costUsd).slice(0, 8).map(s => (
            <li key={s.service}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-white/80 truncate pr-2">{s.service}</span>
                <span className="text-white/60 font-mono tabular-nums flex-shrink-0">{usd(s.costUsd)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden"><div className="h-full bg-emerald-500/60" style={{ width: `${(s.costUsd / max) * 100}%` }} /></div>
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-white/40 leading-relaxed mt-3">{gcp.note}</p>
      </div>
    );
  }
  return (
    <div className="bg-white/[0.03] border border-dashed border-white/15 rounded-xl p-4">
      <p className="text-[10px] uppercase tracking-wider text-white/50 font-mono flex items-center gap-1.5 mb-2"><Cloud className="w-4 h-4 text-white/40" /> GCP Cloud spend · not connected</p>
      <p className="text-sm text-white/70 mb-3">{gcp.reason}</p>
      <p className="text-[11px] uppercase tracking-wider text-white/40 font-mono mb-1.5">Setup steps</p>
      <ol className="space-y-1.5 mb-3">
        {gcp.setupSteps.map((s, i) => (
          <li key={i} className="flex gap-2 text-xs text-white/65"><span className="text-[#FF5C00] font-mono flex-shrink-0">{i + 1}.</span><span className="leading-relaxed">{s}</span></li>
        ))}
      </ol>
      <p className="text-[11px] text-white/40 font-mono">Required secrets: {gcp.requiredEnv.join(", ")}</p>
    </div>
  );
}

// =============================================================================
// Risk-mitigation email composer + sent audit
// =============================================================================
function RiskEmailSection({ breaker }: { breaker: BreakerState }) {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [extra, setExtra] = useState("");
  const [subject, setSubject] = useState("Action needed: AI/cloud spend nearing the daily ceiling");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [resultErr, setResultErr] = useState(false);
  const [audit, setAudit] = useState<RiskEmailRow[]>([]);

  const loadAudit = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}api/admin/spend/risk-emails`, { headers: authHeaders() });
      if (r.ok) { const j = await r.json(); setAudit(j.riskEmails || []); }
    } catch { /* audit list is best-effort */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}api/team-members`, { headers: authHeaders() });
        if (r.ok) setTeam(await r.json());
      } catch { /* picker is best-effort; free-text still works */ }
    })();
    loadAudit();
  }, [loadAudit]);

  // Prefill a sensible default body from current breaker posture.
  useEffect(() => {
    if (body) return;
    setBody(
      `Heads up — our estimated AI spend today is ${usd(breaker.spentToday)} against a ${usd(breaker.ceilingUsd)} daily ceiling (warn at ${usd(breaker.warnUsd)}).\n\n` +
      `These are estimates derived from token counts and a code price table, not a provider invoice.\n\n` +
      `Please review non-essential AI/cron usage and confirm whether we should raise the ceiling, halt, or let it ride.`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breaker.spentToday, breaker.ceilingUsd, breaker.warnUsd]);

  const toggle = (email: string) => setPicked(p => {
    const n = new Set(p);
    n.has(email) ? n.delete(email) : n.add(email);
    return n;
  });

  const recipients = (() => {
    const fromExtra = extra.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    const set = new Set<string>([...picked, ...fromExtra].map(e => e.toLowerCase()));
    return [...set];
  })();

  const send = async () => {
    setResult(null); setResultErr(false);
    if (recipients.length === 0) { setResult("Pick at least one recipient (team member or typed email)."); setResultErr(true); return; }
    if (subject.trim().length === 0) { setResult("Subject is required."); setResultErr(true); return; }
    if (body.trim().length === 0) { setResult("Body is required."); setResultErr(true); return; }
    setSending(true);
    // Idempotency key ties this exact composed message to a 1-minute window so a
    // double-click can't double-send. Recipients added later still go out
    // because the server de-dupes per (idempotencyKey, recipient).
    const idempotencyKey = `risk-${breaker.day}-${Math.floor(Date.now() / 60000)}`;
    try {
      const r = await fetch(`${BASE}api/admin/spend/risk-email`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ recipients, subject: subject.trim(), body: body.trim(), idempotencyKey }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok) {
        setResult(`Sent ${j.sent} · skipped ${j.skipped} (duplicate) · failed ${j.failed}.`);
        setResultErr((j.failed ?? 0) > 0);
        await loadAudit();
      } else if (r.status === 502) {
        setResult(j.error || "All sends failed."); setResultErr(true);
      } else {
        setResult(j.error || `Send failed (${r.status}).`); setResultErr(true);
      }
    } catch { setResult("Network error."); setResultErr(true); }
    setSending(false);
  };

  return (
    <div className="bg-white/[0.03] border border-white/[0.1] rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Send className="w-4 h-4 text-[#FF5C00]" />
        <h2 className="text-lg font-semibold text-white">Send risk-mitigation email</h2>
      </div>

      <div className="space-y-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/45 font-mono mb-1.5">Team recipients</p>
          {team.length === 0 ? (
            <p className="text-white/40 text-xs">No team members loaded — use the free-text field below.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {team.map(m => {
                const on = picked.has(m.email);
                return (
                  <button key={m.id} onClick={() => toggle(m.email)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-[#FF5C00] text-white border-[#FF5C00]" : "bg-white/[0.05] text-white/70 border-white/15 hover:border-white/30"}`}>
                    {m.name || m.email} <span className="opacity-60">· {m.role}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/45 font-mono">Additional emails (comma / space separated)</span>
          <input value={extra} onChange={e => setExtra(e.target.value)} placeholder="founder@example.com, ops@example.com" className="w-full mt-1 bg-white/[0.05] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#FF5C00]/50 focus:outline-none" />
        </label>

        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/45 font-mono">Subject</span>
          <input value={subject} onChange={e => setSubject(e.target.value)} maxLength={300} className="w-full mt-1 bg-white/[0.05] border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5C00]/50 focus:outline-none" />
        </label>

        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/45 font-mono">Body</span>
          <textarea value={body} onChange={e => setBody(e.target.value)} maxLength={20000} rows={6} className="w-full mt-1 bg-white/[0.05] border border-white/15 rounded-lg px-3 py-2 text-sm text-white leading-relaxed focus:border-[#FF5C00]/50 focus:outline-none resize-y" />
        </label>

        <ProgressLoader
          active={sending}
          title={`Sending to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}`}
          note="Each recipient is logged and de-duplicated server-side."
          stages={["Validating recipients", "Composing messages", "Handing off to Resend", "Recording audit trail"]}
          stageIntervalMs={2500}
        />

        {result && (
          <div className={`rounded-lg p-3 text-sm flex items-center gap-2 ${resultErr ? "bg-red-500/10 border border-red-500/30 text-red-300" : "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"}`}>
            {resultErr ? <AlertTriangle className="w-4 h-4 flex-shrink-0" /> : <ShieldCheck className="w-4 h-4 flex-shrink-0" />} {result}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[11px] text-white/40 font-mono">{recipients.length} unique recipient{recipients.length === 1 ? "" : "s"}</p>
          <button onClick={send} disabled={sending} className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send risk email</>}
          </button>
        </div>
      </div>

      <div className="pt-3 border-t border-white/[0.08]">
        <p className="text-[10px] uppercase tracking-wider text-white/45 font-mono flex items-center gap-1.5 mb-2"><History className="w-3 h-3" /> Recently sent ({audit.length})</p>
        {audit.length === 0 ? (
          <p className="text-white/40 text-xs">No risk emails sent yet.</p>
        ) : (
          <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {audit.map(row => (
              <li key={row.id} className="bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-white/80 truncate">{row.subject}</p>
                  <p className="text-[10px] text-white/45 font-mono truncate">→ {row.toEmail}</p>
                </div>
                <span className="text-[10px] text-white/40 font-mono flex-shrink-0">{row.sentAt ? new Date(row.sentAt).toLocaleString() : "—"}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
